import { Banner } from '@app/components/core/banner/Banner'
import { PageLayout } from '../../layout/PageLayout'
import { Avatar } from '@app/components/core/avatar/Avatar'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import useMediaQuery from '@app/util/useMediaQuery'
import { useClient, useQuery } from '@based/sdk/react'
import { faPlus, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { useTranslations } from '@i18n/useLanguage'
import { TabContent } from './sections/TabContent'
import { useState } from 'react'
import { RightColumn } from './sections/RightColumn'
import { PageHeader } from '@app/components/blocks/pageHeader/PageHeader'
import { useOpenEmailCompose } from '@app/util/hooks/useOpenEmailCompose'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { ServiceProviderViewModel } from 'functions/app/service-provider/view'
import useTransform from '@app/util/useTransform'
import { useUserContext } from '@app/util/hooks/useUserContext'

export type ServiceProviderPageProps = {
  serviceProviderSlug: string
}

export const ServiceProviderPage = ({
  serviceProviderSlug,
}: ServiceProviderPageProps) => {
  const isMobile = useMediaQuery()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const openEmailCompose = useOpenEmailCompose()
  const client = useClient()
  const transformStyle = useTransform()
  const { user } = useUserContext()
  const translations = useTranslations().app
  const serviceProviderQuery = useQuery('view-service-provider', {
    slug: serviceProviderSlug,
  })
  const serviceProvider: ServiceProviderViewModel | undefined =
    serviceProviderQuery.data

  const isFollowingQuery = useQuery(
    user?.id && serviceProvider?.id ? 'is-following' : null,
    {
      entityType: 'serviceProvider',
      entityId: serviceProvider?.id,
      userId: user?.id,
    },
  )
  const isFollowing: boolean = isFollowingQuery.data ?? false

  const toggleFollow = () => {
    void client.call('follow-service-provider', {
      serviceProviderId: serviceProvider?.id,
    })
  }

  if (serviceProviderQuery.loading) {
    return <div>{translations.serviceProviderPage.loading}</div>
  }

  if (!serviceProvider) {
    return <div>{translations.serviceProviderPage.notFound}</div>
  }

  if (isMobile) {
    return (
      <>
        <PageLayout gap="0rem">
          <Column alignItems="stretch" gap={isMobile ? '1rem' : '1.5rem'}>
            <Banner image={serviceProvider.banner?.src}>
              <Avatar
                image={serviceProvider.avatar?.src}
                size="large"
                shape="square"
              />
            </Banner>
            <PageHeader
              title={serviceProvider.name}
              subtitle={serviceProvider.intro}
              tags={serviceProvider.type.map(({ name }) => ({
                label: name,
                variant: 'blueMuted',
                size: 'small',
              }))}
              buttons={[
                {
                  label: translations.common.share,
                  onClick: () => setIsShareModalOpen(true),
                  variant: 'secondary',
                  size: 'small',
                },
                {
                  label: isFollowing
                    ? translations.common.following
                    : translations.common.follow,
                  icon: isFollowing ? faCheck : faPlus,
                  onClick: toggleFollow,
                  variant: 'secondary',
                  size: 'small',
                },
                {
                  label: translations.common.contact,
                  onClick: () =>
                    openEmailCompose(serviceProvider.contact?.email),
                  variant: 'primary',
                  size: 'small',
                },
              ]}
            />
          </Column>
          <TabContent serviceProvider={serviceProvider} />
        </PageLayout>

        <SocialShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          title={serviceProvider.name}
        />
      </>
    )
  }

  return (
    <>
      <PageLayout gap="2rem">
        <Column gap="1.5rem" alignItems="stretch">
          <Banner image={serviceProvider.banner?.src}>
            <Avatar
              image={serviceProvider.avatar}
              size="large"
              shape="square"
            />
          </Banner>

          <PageHeader
            title={serviceProvider.name}
            subtitle={serviceProvider.intro}
            tags={serviceProvider.type?.map(({ name }) => ({
              label: name,
              variant: 'blueMuted',
              size: 'small',
            }))}
            buttons={[
              {
                label: translations.common.share,
                onClick: () => setIsShareModalOpen(true),
                variant: 'secondary',
              },
              {
                label: isFollowing
                  ? translations.common.following
                  : translations.common.follow,
                icon: isFollowing ? faCheck : faPlus,
                onClick: toggleFollow,
                variant: 'secondary',
              },
              {
                label: translations.common.contact,
                onClick: () => openEmailCompose(serviceProvider.contact?.email),
                variant: 'primary',
              },
            ]}
          />

          <Row
            gap="3rem"
            style={{
              alignItems: 'flex-start',
              ...transformStyle,
            }}
          >
            <Column alignItems="stretch" style={{ flex: 1, width: '100%' }}>
              <TabContent serviceProvider={serviceProvider} />
            </Column>
            <RightColumn serviceProvider={serviceProvider} />
          </Row>
        </Column>
      </PageLayout>

      <SocialShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={serviceProvider.name}
      />
    </>
  )
}
